<script setup lang="ts">
    import * as yup from 'yup';
    import { planList } from '~/models/enums';

    const store = useOwnerRegisterStore();
    const { formTwoData, $setFormTwoData } = store;

    const phoneRegExp = /^([1-9]{1}[0-9]{9})$/;
        
    const form = ref(null);
    const creditCardDisabled: Ref<boolean> = ref(true);

    const schema = yup.object().shape({
        name: yup.string().required('Store Name is required'),
        address1: yup.string().required('Address is required'),
        region_id: yup.object().shape({
            label: yup.string().required(),
            value: yup.mixed().required(),
        }).required('Region is required'),
        city_id: yup.object().shape({
            label: yup.string().required(),
            value: yup.mixed().required(),
        }).required('City is required'),
        barangay_id: yup.object().shape({
            label: yup.string().required(),
            value: yup.mixed().required(),
        }).required('State is required'),
        categories: yup.array().min(0).max(2),
        // position: yup.string().required('Position is required'),
        contact: yup.string().matches(phoneRegExp, "Invalid Phone Format").required('Phone is required').max(10),
        planType: yup.object().shape({
            label: yup.string().required(),
            value: yup.mixed().required(),
        }).required('Plan Type is required'),
        // paymentMethod: yup.string().required('Payment Method is required'),
        card_number: yup.string().when([], {
            is: () => !creditCardDisabled.value,
            then: schema => schema.required('Credit Card Number is required'),
            otherwise: schema => schema.nullable(),
        }),
        exp_month: yup.string().when([], {
            is: () => !creditCardDisabled.value,
            then: schema => schema.required('Expiry Month is required').notOneOf(['MM'], 'Please select a month'),
            otherwise: schema => schema.nullable(),
        }),
        exp_year: yup.string().when([], {
            is: () => !creditCardDisabled.value,
            then: schema => schema.required('Expiry Year is required').notOneOf(['YY'], 'Please select a year'),
            otherwise: schema => schema.nullable(),
        }),
        card_holder_name: yup.string().when([], {
            is: () => !creditCardDisabled.value,
            then: schema => schema.required('Cardholder name is required'),
            otherwise: schema => schema.nullable(),
        }),
        cvc: yup.string().when([], {
            is: () => !creditCardDisabled.value,
            then: schema => schema.required('CVV is required'),
            otherwise: schema => schema.nullable(),
        }),
    })

    const { handleSubmit, values, setValues, setFieldValue } = useForm({
        validationSchema: schema,
        initialValues: formTwoData
    });

    if(
        !formTwoData || formTwoData?.planType === null || formTwoData?.planType.value === 1
    ) {
        creditCardDisabled.value = true;
    }
    else {
        creditCardDisabled.value = false;
    }

    // setValues({
    //     planType: {
    //         label: "Basic Plan (P0/month)",
    //         value: 1
    //     },
    // }, false)

    const expMonth = ref<number | null>(null);
    const expYear = ref<number | null>(null);

    const handleExpMonthChange = (month: number | null) => {
    expMonth.value = month;
    setFieldValue('exp_month', month);
};

    const handleExpYearChange = (year: number | null) => {
        expYear.value = year;
        setFieldValue('exp_year', year);
    };

    const submitted = ref(false);

    const onInvalidSubmit = ({ errors }: any) => {
        // console.log('invalid', errors)
        submitted.value = true;
        const firstErrorFieldName = Object.keys(errors)[0];
        scrollToValidation(firstErrorFieldName);
    }

    const onSuccess = (values: any) => {
        console.log('from step 2 form', values);
        $setFormTwoData(values);
        handleValidSubmitEvent();
    }

    const scrollToValidation = (errorName: any) => {
        const el = document.querySelector(`[name="${errorName}"]`);
        if (el) {
            el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };

    const regionList = ref()
    const cityList = ref()
    const stateList = ref()

    const submit = handleSubmit(onSuccess, onInvalidSubmit)

    // Exposes the submit button to the parent.
    // For parent access
    defineExpose({
        submit,
    });

    const emit = defineEmits<{
        (e: 'submitEvent'): void,
    }>()

    const handleValidSubmitEvent = () => {
        emit('submitEvent') // Emit the event when the button is clicked
    }

    const callRegionAPI = async() => {
        try {
            const { data, status }: any = await useApi(`/api/value-list/regions`, {
                method: 'GET',
            })

                if(data && status == 'success') {
                    regionList.value = data.map((u: any) => {
                        return {
                            label: u.name,
                            value: u.id,
                        }
                    })
                }
        } catch(error: any) {

            if (error.response) {
                const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

                console.error('Error', {
                    statusCode: statusCode,
                    errorMessage: errorMessage,
                    serverErrorMessage: serverErrorMessage,
                    serverErrorObject: serverErrorObject
                })

                showNotificationError(serverErrorMessage);

            } else {
                console.error("Unexpected error:", error);
            }
        }
    }

    const callCitiesAPI = async(region_id: any) => {
        try {
            const { data, status }: any = await useApi(`/api/value-list/cities?region_id=${region_id}`, {
                method: 'GET',
            })

            if(data && status == 'success') {
                cityList.value = data.map((u: any) => {
                    return {
                        label: u.name,
                        value: u.id,
                    }
                })
            }
        } catch(error: any) {

            if (error.response) {
                const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

                console.error('Error', {
                    statusCode: statusCode,
                    errorMessage: errorMessage,
                    serverErrorMessage: serverErrorMessage,
                    serverErrorObject: serverErrorObject
                })

                showNotificationError(serverErrorMessage);

            } else {
                console.error("Unexpected error:", error);
            }
        }
    }

    const callBarangayAPI = async(city_id: number) => {
        try {
            const { data, status }: any = await useApi(`/api/value-list/barangays?city_id=${city_id}`, {
                method: 'GET',
            })

                if(data && status == 'success') {
                    stateList.value = data.map((u: any) => {
                        return {
                            label: u.name,
                            value: u.id,
                        }
                    })
                }
            } 
        catch(error: any) {

            if (error.response) {
                const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

                console.error('Error', {
                    statusCode: statusCode,
                    errorMessage: errorMessage,
                    serverErrorMessage: serverErrorMessage,
                    serverErrorObject: serverErrorObject
                })

                showNotificationError(serverErrorMessage);

            } else {
                console.error("Unexpected error:", error);
            }
        }
    }

    callRegionAPI();

    const emitRegionChange = ($event: any) => {
        if($event == null) {
            setValues({
                region_id: null,
                city_id: null,
                barangay_id: null
            }, false)
            return;
        }
        callCitiesAPI($event.value)
    }

    const emitCityChange = ($event: any) => {
        callBarangayAPI($event.value)
    }

    const emitPlanChange = ($event: any) => {
        if($event == null || $event.value == 1) {
            creditCardDisabled.value = true;
        }
        else {
            creditCardDisabled.value = false;
        }

        // console.log('plan change', $event);
    }

</script>

<template>
    <form
        ref="form"
        @submit="submit">
        <Block class="mt-[2.4rem] border-[lightGray] border-[.1rem] py-[2.4rem] flex flex-col justify-center items-center rounded-[.8rem]">
            <Block class="max-w-[84rem] w-full mx-auto max-md:px-[1.6rem]">
                <TitleText
                    class="text-center"
                    type="4">
                    Store Information
                </TitleText>
                <p class="text-[1.4rem] leading-[1.5] mt-[2.4rem]">Fields marked with <span class="text-[redEmphasis]">*</span> are required.</p>
                <Block class="mt-[2.4rem]">
                    <Block class="flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="email">Store Name <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="name"
                                icon="none"
                                inputType="text"
                                placeholder="Store Name"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="email">Address <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="address1"
                                icon="none"
                                inputType="text"
                                placeholder="Address"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex items-center flex-1 gap-[.8rem] max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="region">City/State <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex w-full items-start">
                            <Block class="flex flex-wrap w-full">
                                <Dropdown 
                                    class="placeholder-[secondary] w-full font-normal text-[1.4rem]  !rounded-[1rem]"
                                    inputName="region_id"
                                    :model="regionList"
                                    @change="emitRegionChange($event)"
                                    placeholder="Region"
                                />
                            </Block>
                            <Block class="flex flex-wrap w-full ml-[1rem]">
                                <Dropdown 
                                    class="placeholder-[secondary] w-full font-normal text-[1.4rem]  !rounded-[1rem]"
                                    inputName="city_id"
                                    :model="cityList"
                                    :disabled="!values.region_id"
                                    @change="emitCityChange($event)"
                                    placeholder="City/Municipality"
                                />
                            </Block>
                            <Block class="flex flex-wrap w-full ml-[1rem]">
                                <Dropdown 
                                    class="placeholder-[secondary] w-full font-normal text-[1.4rem]  !rounded-[1rem]"
                                    inputName="barangay_id"
                                    :model="stateList"
                                    :disabled="!values.city_id"
                                    placeholder="Barangay"
                                />
                            </Block>
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="categories">Categories <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <DropdownCategory
                                class="placeholder-[secondary] w-full font-normal text-[1.4rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="categories"
                                placeholder="Select Categories"
                            />
                        </Block>
                    </Block>
<!-- 
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="position">Position <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full flex-col">
                            <Radio 
                                inputName="position"
                                :options="positionList"
                                :multiple="true"
                            />
                        </Block>
                    </Block> -->
    
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="contact">Store Phone Number <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPhone
                                class="border-lightGray border-solid border-[1px] rounded-[1rem] overflow-hidden"
                                inputName="contact"
                                placeholder="00000000000"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="planType">Plan Type <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <Dropdown
                                class="placeholder-[secondary] w-full font-normal text-[1.4rem]  !rounded-[1rem]"
                                inputName="planType"
                                :model="planList"
                                @change="emitPlanChange($event)"
                                placeholder="Select Plan"
                            />
                        </Block>
                    </Block>
                </Block>
            </Block>
        </Block>

        <Block v-if="!creditCardDisabled" class="mt-[2.4rem] border-[lightGray] border-[.1rem] py-[2.4rem] flex flex-col justify-center items-center rounded-[.8rem]">
            <Block class="max-w-[84rem] w-full mx-auto max-md:px-[1.6rem]">
                <TitleText
                    class="text-center"
                    type="4">
                    Credit Card Information
                </TitleText>
                <p class="text-[1.4rem] leading-[1.5] mt-[2.4rem]">Fields marked with <span class="text-[redEmphasis]">*</span> are required.</p>
                <Block class="mt-[2.4rem]">
                    <!-- <Block class="flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="phone">Payment Method <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap flex-col w-full">
                            <Radio 
                                inputName="paymentMethod"
                                :options="paymentList"
                                :multiple="true"
                            />
                        </Block>
                    </Block> -->
                    <!-- mt-[1.6rem]  -->
                    <Block class="flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="email">Credit Card Number <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="card_number"
                                icon="none"
                                inputType="text"
                                :maxLength="16"
                                placeholder="00000000000"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex items-center flex-1 gap-[.8rem] max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="firstname">Expiry Date <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex w-full items-start">
                            <Block class="grid-cols-[repeat(2,_minmax(0,_1fr))] grid w-full gap-1.5">
                                    <DropdownDate
                                        inputName="exp_month" 
                                        type="month" 
                                        placeholder="MM" 
                                        icon="calendar"
                                        @changeEvent="handleExpMonthChange"
                                        :submitted="submitted"
                                        class="w-full h-[4rem]"
                                    />
                                    <DropdownDate
                                        inputName="exp_year" 
                                        type="year" 
                                        dateType="future" 
                                        placeholder="YY" 
                                        icon="calendar"
                                        :submitted="submitted"
                                        @changeEvent="handleExpYearChange"
                                        class="w-full h-[4rem]"
                                    />
                                
                            </Block>
                            <!-- <Block class="flex flex-wrap w-full ml-[1rem] "> -->
                                <!-- <DatePicker
                                    class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                    inputName="exp_year"
                                    pickerType="year"
                                    format="YYYY"
                                    :minDate="new Date()"
                                    mode="year"
                                    placeholder="Select Year"
                                /> -->
                                
                            <!-- </Block> -->
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="phone">Credit Cardholder Name <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="card_holder_name"
                                icon="none"
                                inputType="text"
                                placeholder="Credit Card Holder Name"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="phone">Security Code (CVV) <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="cvc"
                                icon="none"
                                :maxLength="3"
                                inputType="text"
                                placeholder="000"
                            />
                        </Block>
                    </Block>
                </Block>
            </Block>
        </Block>
    </form>
</template>